import type { MinorStatus } from '@galapagos/shared';
import type { SignupProgramOption } from '@galapagos/shared/registration';
import { sponsorRewardsOn, type LinkIncentive } from '@/services/link-incentive';
import type { SignupSponsorFeatures } from '../services/signup-api/signup-api';
import { SIGNUP_STEPS, type SignupStepId } from '../steps';
import { LAST_WRAP_UP_STEP } from './wrap-up-steps';

/**
 * What decides the wrap-up path: the sponsor's switches, the HDHP read, the
 * server's minor status, and whether the member's programs are open to them.
 */
export interface WrapUpContext {
  hdhpRequired: boolean;
  callChoice: boolean;
  rewards: boolean;
  languagePreference: boolean;
  /** The sponsor offers Spanish, so the signup language is recorded as the call language. */
  spanish: boolean;
  /** The server says the member is a minor (below their state's age of majority). */
  minor: boolean;
  /** Every program the member chose is closed to them by age. */
  programsUnavailable: boolean;
  /** The sponsor's members book an onboarding call at the end of signup. */
  onboardingCall: boolean;
}

/**
 * Whether every program the member chose is closed to them because they are
 * below its age (the server's list). The member's state never closes a
 * program: there is counseling coverage in every state, and a member is still
 * eligible for a program's app-only experience. Sub-type values in the
 * selection are not programs and never count. Keys are compared by equality
 * only. With no program chosen nothing is closed.
 */
export function selectedProgramsUnavailable(
  options: readonly Pick<SignupProgramOption, 'programKey'>[],
  selection: readonly string[] | null,
  ineligibleProgramKeys: readonly string[],
): boolean {
  const offered = new Set(options.map((option) => option.programKey));
  const programKeys = (selection ?? []).filter((value) => offered.has(value));
  if (programKeys.length === 0) return false;
  const tooYoung = new Set(ineligibleProgramKeys);
  return programKeys.every((key) => tooYoung.has(key));
}

/**
 * Folds the reads into a context. A read that failed or has not answered
 * counts as off: a member whose sponsor or notice could not be read still
 * gets to book, and a failed profile read routes as an adult (the server's
 * parental-consent hold is the safety net).
 * The onboarding call is the exception: with no sponsor answer, or one that
 * does not say, the member keeps it. The link's no-incentive code turns the
 * rewards steps off for every sponsor; no code turns them on.
 */
export function toWrapUpContext(
  features: SignupSponsorFeatures | undefined,
  hdhpRequired: boolean | undefined,
  minorStatus?: MinorStatus,
  programsUnavailable = false,
  incentive?: LinkIncentive,
): WrapUpContext {
  return {
    minor: minorStatus === 'minor',
    hdhpRequired: hdhpRequired ?? false,
    callChoice: features?.callChoice ?? false,
    rewards: sponsorRewardsOn(features?.rewards, incentive),
    languagePreference: features?.languagePreference ?? false,
    spanish: features?.spanish ?? false,
    programsUnavailable,
    onboardingCall: features?.onboardingCall ?? true,
  };
}

/**
 * Where a member headed for a call goes next: the interpreter question when
 * asked, else booking. Minors go straight to booking, as in production. A
 * sponsor without the onboarding call has no call to head for, so the member
 * leaves the wrap-up.
 */
export function callPathStep(context: WrapUpContext): SignupStepId {
  if (!context.onboardingCall) return wrapUpExitStep(context);
  if (context.minor) return 'booking';
  return context.languagePreference ? 'language-preference' : 'booking';
}

/**
 * The first wrap-up screen, in the legacy signup's order: the HDHP notice
 * comes first and skips the call-or-chat question; rewards only show when the
 * call-or-chat question does. A sponsor without the onboarding call skips the
 * whole segment, the HDHP notice included, since that notice offers the call.
 * The call-or-chat question needs a program still open to the member: when
 * every chosen program is closed by age, the member is sent to a call.
 */
export function firstWrapUpStep(context: WrapUpContext): SignupStepId {
  if (!context.onboardingCall) return wrapUpExitStep(context);
  if (context.hdhpRequired) return 'hdhp-notice';
  // Minors skip rewards and the call-or-chat question, as in production.
  if (context.minor) return 'booking';
  if (context.callChoice && !context.programsUnavailable) {
    return context.rewards ? 'rewards-intro' : 'call-choice';
  }
  return callPathStep(context);
}

/**
 * Where every path out of the wrap-up lands. A minor ends on "successfully
 * registered", with no download step. Everyone else goes to the step right
 * after the segment, so whichever step follows it (today the download page)
 * takes over without this segment changing.
 */
export function wrapUpExitStep(context?: Pick<WrapUpContext, 'minor'>): SignupStepId {
  if (context?.minor) return 'registered';
  const exit = SIGNUP_STEPS[SIGNUP_STEPS.indexOf(LAST_WRAP_UP_STEP) + 1];
  if (exit === undefined) throw new Error('The signup wrap-up must be followed by another step');
  return exit;
}
